import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Statistic, DatePicker, Table, message } from 'antd';
import { BarChartOutlined, UserOutlined, DollarOutlined, ShoppingOutlined, LineChartOutlined, TrophyTwoTone, FireTwoTone } from '@ant-design/icons';
import { Column, Line, Bar } from '@ant-design/charts';
import { AnalyticsApi } from 'src/api';
import type { IOrderStatusAnalytics, IMonthlyAnalytics, IOrderTypeAnalytics, IGeneralStats, IProductAnalytics } from 'src/api/AnalyticsApi';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export const StartPage: React.FC = () => {
  // Метрики
  const [generalStats, setGeneralStats] = useState<IGeneralStats | null>(null);
  // Статуси
  const [statusAnalytics, setStatusAnalytics] = useState<IOrderStatusAnalytics[]>([]);
  // Типи
  const [typeAnalytics, setTypeAnalytics] = useState<IOrderTypeAnalytics[]>([]);
  // Місяці
  const [monthlyAnalytics, setMonthlyAnalytics] = useState<IMonthlyAnalytics[]>([]);
  // Топ продукти
  const [productAnalytics, setProductAnalytics] = useState<IProductAnalytics[]>([]);
  // Loading
  const [loading, setLoading] = useState(true);
  // Дата фільтр
  const [dateRange, setDateRange] = useState<Dayjs[] | null>(null);

  // Завантаження даних
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = dateRange && dateRange.length === 2 ? {
        dateFrom: dateRange[0].format('YYYY-MM-DD'),
        dateTo: dateRange[1].format('YYYY-MM-DD')
      } : undefined;
      const year = dateRange && dateRange[0] ? dateRange[0].year().toString() : dayjs().year().toString();
      const [generalRes, statusRes, typeRes, monthlyRes, productRes] = await Promise.all([
        AnalyticsApi.getGeneralStats(params),
        AnalyticsApi.getOrderStatusAnalytics(params),
        AnalyticsApi.getOrderTypeAnalytics(params),
        AnalyticsApi.getMonthlyAnalytics({ year }),
        AnalyticsApi.getProductAnalytics(params)
      ]);
      if (generalRes?.data) setGeneralStats(generalRes.data);
      if (statusRes?.data) setStatusAnalytics(statusRes.data);
      if (typeRes?.data) setTypeAnalytics(typeRes.data);
      if (monthlyRes?.data) setMonthlyAnalytics(monthlyRes.data);
      if (productRes?.data) setProductAnalytics(productRes.data);
    } catch (e) {
      message.error('Помилка завантаження аналітики');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Колонки для таблиць
  const statusColumns = [
    { title: 'Статус', dataIndex: 'status_name', key: 'status_name' },
    { title: 'Кількість', dataIndex: 'count', key: 'count' },
    { title: 'Загальна сума', dataIndex: 'total_amount', key: 'total_amount', render: (v: number) => v?.toLocaleString() + ' грн' },
  ];
  const typeColumns = [
    { title: 'Тип', dataIndex: 'type', key: 'type' },
    { title: 'Кількість', dataIndex: 'count', key: 'count' },
    { title: 'Загальна сума', dataIndex: 'total_amount', key: 'total_amount', render: (v: number) => v?.toLocaleString() + ' грн' },
  ];
  const monthlyColumns = [
    { title: 'Місяць', dataIndex: 'month', key: 'month' },
    { title: 'Кількість', dataIndex: 'count', key: 'count' },
    { title: 'Загальна сума', dataIndex: 'total_amount', key: 'total_amount', render: (v: number) => v?.toLocaleString() + ' грн' },
    { title: 'Середня сума', dataIndex: 'avg_amount', key: 'avg_amount', render: (v: number) => v?.toLocaleString() + ' грн' },
  ];
  const productColumns = [
    { title: 'Продукт', dataIndex: 'product_name', key: 'product_name' },
    { title: 'Артикул', dataIndex: 'product_model', key: 'product_model' },
    { title: 'Кількість', dataIndex: 'count', key: 'count' },
    { title: 'Середня ціна', dataIndex: 'avg_price', key: 'avg_price', render: (v: number) => v?.toLocaleString() + ' грн' },
    { title: 'Дохід', dataIndex: 'total_revenue', key: 'total_revenue', render: (v: number) => v?.toLocaleString() + ' грн' },
  ];

  // Кольори та іконки для метрик
  const metrics = [
    { title: 'Всього замовлень', value: generalStats?.total_orders, icon: <BarChartOutlined style={{ color: '#1890ff', fontSize: 36 }} />, color: '#e6f7ff' },
    { title: 'Загальна сума', value: generalStats?.total_amount?.toLocaleString() + ' грн', icon: <DollarOutlined style={{ color: '#52c41a', fontSize: 36 }} />, color: '#f6ffed' },
    { title: 'Середній чек', value: generalStats?.avg_order_amount?.toLocaleString() + ' грн', icon: <LineChartOutlined style={{ color: '#faad14', fontSize: 36 }} />, color: '#fffbe6' },
    { title: 'Сума тестувань партнерів', value: generalStats?.partner_testing_total?.toLocaleString() + ' грн', icon: <UserOutlined style={{ color: '#722ed1', fontSize: 36 }} />, color: '#f9f0ff' },
  ];

  // eslint-disable-next-line react/jsx-handler-names
  const handleDateRangeChange = (dates: (Dayjs | null)[] | null): void => {
    if (!dates || !dates[0] || !dates[1]) setDateRange(null);
    else setDateRange([dates[0], dates[1]].filter(Boolean) as Dayjs[]);
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Фільтр по датах */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <RangePicker onChange={handleDateRangeChange} style={{ minWidth: 260 }} />
      </div>
      {/* 1. Картки-метрики */}
      <Row gutter={16} style={{ marginBottom: 32 }}>
        {metrics.map((m, idx) => (
          <Col span={6} key={m.title}>
            <Card style={{ background: m.color, borderRadius: 12, textAlign: 'center' }}>
              {m.icon}
              <Statistic title={m.title} value={m.value ?? '-'} valueStyle={{ marginTop: 12, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>
      {/* 2. Розподіл по статусах */}
      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col span={12}>
          <Card title={<><FireTwoTone twoToneColor="#f5222d" /> Розподіл по статусах</>} loading={loading}>
            <Column data={statusAnalytics} xField="status_name" yField="count" height={300} color="#f5222d" />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Деталі по статусах" loading={loading}>
            <Table dataSource={statusAnalytics} columns={statusColumns} pagination={false} size="small" rowKey="status_name" />
          </Card>
        </Col>
      </Row>
      {/* 3. Розподіл по типах */}
      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col span={12}>
          <Card title={<><TrophyTwoTone twoToneColor="#faad14" /> Розподіл по типах</>} loading={loading}>
            <Column data={typeAnalytics} xField="type" yField="count" height={300} color="#faad14" />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Деталі по типах" loading={loading}>
            <Table dataSource={typeAnalytics} columns={typeColumns} pagination={false} size="small" rowKey="type" />
          </Card>
        </Col>
      </Row>
      {/* 4. Динаміка по місяцях */}
      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col span={12}>
          <Card title={<><LineChartOutlined style={{ color: '#1890ff' }} /> Динаміка по місяцях</>} loading={loading}>
            <Line data={monthlyAnalytics} xField="month" yField="total_amount" height={300} color="#1890ff" />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Деталі по місяцях" loading={loading}>
            <Table dataSource={monthlyAnalytics} columns={monthlyColumns} pagination={false} size="small" rowKey="month" />
          </Card>
        </Col>
      </Row>
      {/* 5. Топ-10 продуктів */}
      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col span={12}>
          <Card title={<><ShoppingOutlined style={{ color: '#13c2c2' }} /> Топ-10 продуктів</>} loading={loading}>
            <Bar data={productAnalytics.slice(0, 10)} xField="total_revenue" yField="product_name" height={300} color="#13c2c2" />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Деталі по продуктах" loading={loading}>
            <Table dataSource={productAnalytics.slice(0, 10)} columns={productColumns} pagination={false} size="small" rowKey="product_name" />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
